import {
  type LegacyRiskGapStatus,
  type RiskGapModel,
  isRiskGapUserStatus,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { notify } from '@ctw/shared/components/toast';
import { confidenceLevelToRating } from '@ctw/shared/content/risk/risk-gap-confidence-level';
import { RiskGapInfoIcon, infoForRiskGapIcon } from '@ctw/shared/content/risk/risk-gap-info-icon';
import { riskGapStatusBadgeDefinitionV2 } from '@ctw/shared/content/risk/risk-gap-status-badge-definition-v2';
import {
  RiskGapStatusDialogContent,
  type RiskGapStatusDialogStatus,
} from '@ctw/shared/content/risk/risk-gap-status-dialog';
import { type CtwFetchFn, useCTW } from '@ctw/shared/context/ctw-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faArrowLeft, faFire, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { RISK_GAP_VALUES } from '@zeus-health/types';
import { ReactJason } from 'react-jason';
import { RiskGapEvidence } from './risk-gap-evidence';
import { RiskGapStatusFormV2 } from './risk-gap-status-form-v2';
import { riskGapTypeBadgeDefinition } from './risk-gap-type-badge-definition';

export interface RiskGapDetailViewProps {
  riskGap: RiskGapModel;
  onClose?: () => void;
}

/** Risk Gap Side Panel Detail view used exclusively in v2 of the UI */
export const RiskGapDetailView = ({ riskGap, onClose }: RiskGapDetailViewProps) => {
  const { breakpoints } = useBreakpointQuery();
  const { requestContext, ctwFetch } = useCTW();
  const queryClient = useQueryClient();
  const typeBadge = riskGapTypeBadgeDefinition(riskGap.type);
  const statusBadge = riskGapStatusBadgeDefinitionV2(riskGap);
  const confidenceRating =
    riskGap.type !== 'recapture' ? confidenceLevelToRating(riskGap.confidenceLevel) : undefined;
  let confidenceBadge:
    | {
        content: string;
      }
    | undefined;
  if (confidenceRating === 'high') {
    confidenceBadge = { content: 'High confidence' };
  } else if (confidenceRating === 'medium') {
    confidenceBadge = { content: 'Medium confidence' };
  } else if (confidenceRating === 'low') {
    confidenceBadge = { content: 'Low confidence' };
  }
  const statusSectionClassName = twx('p-4', {
    'bg-info-background': riskGap.type === 'net-new',
    'bg-caution-background': riskGap.type === 'suspect',
    'bg-success-background': riskGap.type === 'recapture',
  });
  const telemetry = useTelemetry();
  const { openModal: openViewFHIRModal } = useModal({
    boundaryName: 'ViewFHIRModal',
    layout: { fullbleed: false, mode: 'full', overflowBehavior: 'scroll', showCloseButton: true },
  });
  const { openModal: openStatusEditModal, closeModal: closeStatusEditModal } = useModal({
    boundaryName: 'StatusDialogModal',
    layout: { fullbleed: false, mode: 'fit', overflowBehavior: 'scroll', showCloseButton: false },
  });

  const handleViewFHIR = () => {
    openViewFHIRModal({
      telemetry,
      action: {
        content: {
          body: (
            <>
              <Heading level="h2" className={tw`text-lg`}>
                View FHIR
              </Heading>
              <div className={tw`h-full text-sm [&_pre]:overflow-auto`}>
                <ReactJason value={riskGap.resource} />
              </div>
            </>
          ),
        },
      },
    });
  };

  const handleEditSubstatuses = () => {
    openStatusEditModal({
      telemetry,
      action: {
        content: {
          body: (
            <RiskGapStatusDialogContent
              status={toRiskGapStatusDialogStatus(riskGap.status)}
              note={riskGap.lastStatusUpdate.note ?? ''}
              selectedSubstatuses={riskGap.substatuses}
              onConfirm={async (substatuses, pendingNote) => {
                await handleSubmit(
                  pendingNote,
                  toRiskGapStatusDialogStatus(riskGap.status),
                  substatuses,
                );
                closeStatusEditModal();
              }}
              onCancel={closeStatusEditModal}
            />
          ),
        },
      },
    });
  };

  const handleSubmit = (
    notes: string,
    status: RiskGapStatusDialogStatus,
    substatuses: Array<string> = [],
  ) => {
    return new Promise<void>((resolve, reject) => {
      void ctwFetch(
        `${getStandaloneBaseUrl(requestContext.env)}/api/risk-gaps/${riskGap.id}/status-updates`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
          },
          body: JSON.stringify({
            data: {
              type: 'risk-gap-status-update',
              attributes: {
                status,
                substatuses,
                note: notes,
              },
            },
          }),
        },
      )
        .then((fetchResponse: Awaited<ReturnType<CtwFetchFn>>) => {
          if (fetchResponse.ok) {
            notify({
              type: 'success',
              title: 'The status has been updated.',
            });
          } else {
            telemetry.trackError({
              error: new Error('Error updating Risk Gap status'),
              context: {
                data: JSON.stringify(fetchResponse.data),
                headers: JSON.stringify(fetchResponse.headers),
              },
            });
            notify({
              type: 'error',
              title: 'Unable to update the risk gap status.',
              body: 'Please try again later or contact support if the issue persists.',
            });
          }
          void queryClient.invalidateQueries();
          resolve();
        })
        .catch((error) => {
          telemetry.trackError({
            error,
          });
          notify({
            type: 'error',
            title: 'Unable to update the risk gap status.',
            body: 'Please try again later or contact support if the issue persists.',
          });
          reject(error as Error);
        });
    });
  };

  function toRiskGapStatusDialogStatus(legacy: LegacyRiskGapStatus): RiskGapStatusDialogStatus {
    return isRiskGapUserStatus(legacy) ? legacy : 'open-gap';
  }

  return (
    <div
      className={twx('flex h-full max-h-full w-0 flex-col overflow-hidden bg-white', {
        'w-full max-w-full': riskGap,
      })}
    >
      <div
        className={tw`flex min-h-[65px] w-full items-center justify-between gap-2 overflow-visible border-b border-b-background-subtle px-5 py-3`}
      >
        <div
          className={tw`order-1 flex flex-1 flex-row items-center gap-2 md:order-0 md:flex-col md:items-start lg:flex-row lg:items-center`}
        >
          <h2
            className={tw`order-1 flex font-normal text-content-main sm:text-sm md:text-md lg:order-0 lg:text-lg`}
          >
            {riskGap.title} (HCC {riskGap.hccCode})
          </h2>
          <div className={tw`order-0 flex flex-wrap items-center gap-2 lg:order-1`}>
            <Badge {...typeBadge} size="sm" />
          </div>
        </div>
        {onClose && (
          <Button
            ariaLabel={breakpoints.isAtLeast.lg ? 'Close' : 'Back'}
            type="button"
            size="sm"
            variant="circular"
            className={tw`order-0 flex min-w-8 items-center justify-center rounded-full bg-white p-2 text-content-subtle shadow-white hover:bg-white md:order-1`}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={breakpoints.isAtLeast.md ? faXmark : faArrowLeft} />
          </Button>
        )}
        <Button
          ariaLabel="View FHIR"
          type="button"
          size="sm"
          variant="link"
          className={tw`order-2 flex rounded-b border p-2 text-content-subtle hover:text-content-main`}
          onClick={handleViewFHIR}
          telemetryInteractionName="resource_action_click"
        >
          <FontAwesomeIcon icon={faFire} className={tw`h-4 w-4`} />
        </Button>
      </div>
      {/* Content */}
      <div className={tw`flex flex-1 flex-col overflow-auto `}>
        <div className={`${statusSectionClassName} border-b border-b-background-subtle`}>
          <div className={tw`mb-4`}>
            <div className={tw`flex flex-wrap items-center gap-2`}>
              <Badge
                className={tw`rounded-full border bg-white text-content-main `}
                content={statusBadge.content}
                icon={undefined}
                size="sm"
                variant="meta"
              />
              {confidenceBadge && (
                <Badge
                  className={tw`rounded-full border bg-white text-content-main`}
                  content={confidenceBadge.content}
                  icon={undefined}
                  size="sm"
                  variant="meta"
                />
              )}
              {riskGap.substatuses.map((substatus) => (
                <Badge
                  key={substatus}
                  content={substatus}
                  className={tw`rounded border bg-white`}
                  size="sm"
                  textCasing="capitalize"
                  variant="meta"
                />
              ))}
              <div className={tw`flex items-center justify-center`}>
                <RiskGapInfoIcon
                  className={tw`mr-1 ml-2 pb-1`}
                  hide={
                    riskGap.status !==
                    RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE
                      .EXPECTED_TO_CLOSE_FIRST_PARTY
                  }
                  info={infoForRiskGapIcon(riskGap.status)}
                />
                <span className={tw`flex items-center pt-.6 pl-1 text-content-subtle text-xs`}>
                  {formatFHIRDate(riskGap.lastStatusUpdateDate)} by {riskGap.lastUpdatedByDisplay}
                </span>
              </div>
              {riskGap.status !== 'open-gap' ? (
                <Button
                  type="button"
                  size="xs"
                  variant="link"
                  className={tw``}
                  onClick={handleEditSubstatuses}
                >
                  Edit
                </Button>
              ) : null}
            </div>
          </div>

          {/* ICD-10 Codes */}
          {riskGap.icd10Codes.length > 0 && (
            <div className={tw`mb-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2`}>
              {riskGap.icd10Codes.map((code) => {
                const isPayerSource = code.sources.includes(
                  RISK_GAP_VALUES.MEASURE_REPORT.GROUP.CODE.CODING.CODE.EXTENSION.RISK_GAP_SOURCE
                    .PAYER_GAP,
                );
                return (
                  <div
                    key={code.code}
                    className={tw`flex h-full flex-col gap-1.5 rounded-lg border border-divider-main bg-white p-2.5`}
                  >
                    <div className={tw`text-sm leading-snug`}>
                      <span className={tw`font-bold`}>{code.code}</span>{' '}
                      {code.display && <span>{code.display}</span>}
                    </div>
                    {isPayerSource && (
                      <div className={tw`text-content-subtle text-xs`}>Reported by payer</div>
                    )}
                    {code.lastEncounterDiagnosisRecordedDate && (
                      <div className={tw`mt-auto text-content-subtle text-xs`}>
                        Last recorded {formatFHIRDate(code.lastEncounterDiagnosisRecordedDate)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <RiskGapStatusFormV2
            initialNote={riskGap.lastStatusUpdate.note ?? ''}
            currentStatus={riskGap.status}
            onSave={handleSubmit}
          />
        </div>
        <div className={tw`p-4`}>
          <RiskGapEvidence riskGap={riskGap} />
        </div>
      </div>
    </div>
  );
};
